import React, { useEffect, useRef, useState } from "react";
import { mediaUrl } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

const PLACEHOLDER = "https://images.unsplash.com/photo-1631014304559-24a497774470?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

/**
 * Standard shop gallery: one large image with a thumbnail strip beneath it.
 * Thumbnails switch the main photo; on a touch screen the main photo can be
 * swiped left and right.
 */
export const ProductGallery = ({ images = [], name = "" }) => {
  const shots = images.filter(Boolean).slice(0, 5);
  const [i, setI] = useState(0);
  const touch = useRef(null);

  useEffect(() => { setI(0); }, [images.length, name]);

  const go = (dir) => setI((n) => (shots.length ? (n + dir + shots.length) % shots.length : 0));
  const src = shots.length ? mediaUrl(shots[i]) : PLACEHOLDER;

  return (
    <div data-testid="pd-gallery">
      <div
        className="relative aspect-square rounded-3xl overflow-hidden bg-muted border border-border group"
        onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touch.current === null) return;
          const dx = e.changedTouches[0].clientX - touch.current;
          if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
          touch.current = null;
        }}
        data-testid="pd-gallery-main"
      >
        <img
          src={src}
          alt={name}
          onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
          className="w-full h-full object-cover"
          data-testid="pd-gallery-image"
        />
        {shots.length > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              aria-label="Previous photo"
              data-testid="pd-gallery-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/85 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="Next photo"
              data-testid="pd-gallery-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/85 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronRight size={16} />
            </button>
            <span className="absolute bottom-3 right-4 text-xs bg-background/85 border border-border rounded-full px-2.5 py-1" data-testid="pd-gallery-counter">
              {i + 1} / {shots.length}
            </span>
          </>
        )}
      </div>

      {shots.length > 1 && (
        <div className="flex gap-3 mt-4 overflow-x-auto pb-1" data-testid="pd-gallery-thumbs">
          {shots.map((s, n) => (
            <button
              key={`${s}-${n}`}
              onClick={() => setI(n)}
              aria-label={`Photo ${n + 1}`}
              data-testid={`pd-gallery-thumb-${n}`}
              className={`w-20 h-20 shrink-0 rounded-xl overflow-hidden border-2 transition-colors ${
                n === i ? "border-primary" : "border-border hover:border-primary/40"}`}
            >
              <img src={mediaUrl(s)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
